/**
 * iter165 — Centralised time formatting in NexFlow's local timezone.
 *
 * Field-test bug: a job stamped at 9:45 AM ET was rendering as "ON JOB 1:45 PM"
 * because the backend serialises naive UTC datetimes (no `Z`, no offset). JS
 * `new Date('2026-02-22T13:45:00')` then parses that as LOCAL TIME, so a
 * browser in ET reads it as 1:45 PM ET instead of 9:45 AM ET.
 *
 * Two-part fix in this helper:
 *   1. If the ISO string lacks a TZ marker, treat it as UTC by appending `Z`.
 *   2. Always format via `Intl.DateTimeFormat` with `timeZone: 'America/New_York'`
 *      so we never accidentally render in the user's browser TZ. Honours EDT/EST
 *      automatically — no hardcoded offset.
 *
 * Usage:
 *   import { fmtLocalTime, fmtLocalDate, fmtLocalDateTime, COMPANY_TZ } from '@/lib/timeFmt';
 *   fmtLocalTime(job.actual_start_time)   // "9:45 AM"
 *   fmtLocalDate(job.scheduled_date)      // "Jun 22"
 *   fmtLocalDateTime(...)                 // "Jun 22, 9:45 AM"
 */

export const COMPANY_TZ = 'America/New_York';

const TZ_RE = /(Z|[+-]\d{2}:?\d{2})$/;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Normalise an ISO string so JS Date parses it correctly even if the
 *  backend dropped the trailing `Z`. Mongo-style naive UTC → treated as UTC. */
function toDate(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input);
  // If no Z / offset, assume the backend stored UTC and just forgot the marker.
  let normalised = s;
  if (normalised.includes('T') && !TZ_RE.test(normalised)) normalised = normalised + 'Z';
  const d = new Date(normalised);
  return isNaN(d.getTime()) ? null : d;
}

export function fmtLocalTime(input, opts = {}) {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', {
    timeZone: COMPANY_TZ,
    hour: 'numeric',
    minute: '2-digit',
    ...opts,
  });
}

export function fmtLocalDate(input, opts = {}) {
  if (input == null || input === '') return '';
  // iter168 — Date-only inputs (YYYY-MM-DD) are CALENDAR dates with no time
  // component and must NOT be shifted by timezone. Parse Y/M/D directly and
  // build the display string without ever passing through TZ conversion.
  // This prevents the "invoice dated 6/22 displaying as 6/21" bug for ET users
  // when the backend stores an appointment date as a UTC-midnight ISO string.
  const dateOnly = typeof input === 'string' && DATE_ONLY_RE.exec(input);
  if (dateOnly) {
    const [, y, m, dd] = dateOnly;
    const d = new Date(Date.UTC(Number(y), Number(m) - 1, Number(dd), 12));
    return d.toLocaleDateString('en-US', {
      timeZone: 'UTC', // we built it at UTC noon — render as-built
      month: 'short',
      day: 'numeric',
      ...opts,
    });
  }
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    timeZone: COMPANY_TZ,
    month: 'short',
    day: 'numeric',
    ...opts,
  });
}

export function fmtLocalDateTime(input, opts = {}) {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    timeZone: COMPANY_TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...opts,
  });
}

export default { fmtLocalTime, fmtLocalDate, fmtLocalDateTime, COMPANY_TZ };
